/* eslint-disable complexity */

import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

import Button from 'carbon-react/lib/components/button';

import { FormattedMessage } from '@futrli/localisation';
import { UICarbonNumberField } from '@futrli/ui-components';

import { DayOfMonthMenu } from '../menus/day-of-month-menu/day-of-month-menu';
import { FreestyleContentRow } from './freestyle-content-row/freestyle-content-row';
import { FreestyleMethodCellData, FreestyleMethodRowData } from './types';
import { MAX_DECIMALS, QuickActionsType } from '../types';
import { MAX_PERCENTAGE } from '../../../core/constants';
import { QuickActionsMenu } from '../menus/quick-actions-menu/quick-actions-menu';

import styles from './freestyle-content-form.scss';

interface FreestyleContentFormProps {
  currencySymbol?: string;
  format?: 'currency' | 'decimal';
  isAddingNew?: boolean;
  isDisabled?: boolean;
  isDisabledAddYear?: boolean;
  isDisabledUpdate?: boolean;
  onAddFinancialYear?: () => void;
  onQuickActionsTypeChange?: React.ChangeEventHandler<HTMLInputElement>;
  onQuickActionsUpdate?: () => void;
  onQuickActionsValueChange?: (value: string) => void;
  onSelectAll?: (e: React.ChangeEvent<HTMLInputElement>, year: FreestyleMethodRowData) => void;
  onSelect?: (months: FreestyleMethodCellData[], year: FreestyleMethodRowData) => void;
  onStoreOnTheChange?: React.ChangeEventHandler<HTMLInputElement>;
  onValueChange?: (value: string, month: FreestyleMethodCellData, year: FreestyleMethodRowData) => void;
  pastBlankDataValue?: string;
  quickActionsType?: QuickActionsType;
  quickActionsValue?: string;
  rows?: FreestyleMethodRowData[];
  storeOnThe?: string;
  yearsAdded?: string[];
}

export const FreestyleContentForm = ({
  currencySymbol,
  format,
  isAddingNew = true,
  isDisabled = false,
  isDisabledAddYear = false,
  isDisabledUpdate = false,
  onAddFinancialYear,
  onQuickActionsTypeChange,
  onQuickActionsUpdate,
  onQuickActionsValueChange,
  onSelectAll,
  onSelect,
  onStoreOnTheChange,
  onValueChange,
  pastBlankDataValue,
  quickActionsType,
  quickActionsValue,
  rows = [],
  storeOnThe,
  yearsAdded = []
}: FreestyleContentFormProps) => {
  const intl = useIntl();

  const showPercentageInput =
    quickActionsType === QuickActionsType.decreaseByPercentage ||
    quickActionsType === QuickActionsType.increaseByPercentage;

  const showAmountInput =
    quickActionsType === QuickActionsType.decreaseByAmount ||
    quickActionsType === QuickActionsType.increaseByAmount ||
    quickActionsType === QuickActionsType.setSelectedTo ||
    quickActionsType === QuickActionsType.splitBetweenSelected;

  const [lastQuickUpdatedRows, setLastQuickUpdatedRows] = useState<Record<string, number>>({});

  const handleQuickActionsUpdate = useCallback(() => {
    // Figure out which rows will have been changed
    const changedRows = rows
      .filter(row => row.isAllSelected || row.months.find(month => month.selected))
      .filter(row => row.months.filter(month => month.value !== quickActionsValue).length !== 0);

    // Update lastQuickUpdatedRows to force changed rows to expand
    const changedRowObj: Record<string, number> = {};
    changedRows.forEach(row => (changedRowObj[row.date] = Date.now()));
    setLastQuickUpdatedRows(changedRowObj);

    onQuickActionsUpdate?.();
  }, [onQuickActionsUpdate, quickActionsValue, rows]);

  return (
    <div>
      <div className={styles.menuBarCarbon} data-testid="menuBar">
        <DayOfMonthMenu
          className={clsx(styles.formInput, styles.medium)}
          onChange={onStoreOnTheChange}
          value={storeOnThe}
          disabled={isDisabled}
          label={{
            id: 'predict.store_on_the',
            defaultMessage: 'Store on the'
          }}
          size="medium"
        />
        <div style={{ borderLeft: '1px solid var(--colorsUtilityMajor100)', paddingLeft: '16px' }}>
          <QuickActionsMenu
            className={clsx(styles.formInput, styles.xlarge, styles.mr8)}
            marginRight={8}
            onChange={onQuickActionsTypeChange}
            value={quickActionsType}
            disabled={isDisabled}
            size="medium"
          />
        </div>
        <UICarbonNumberField
          autoComplete="off"
          className={clsx(styles.formInput, styles.small, styles.mr8)}
          onChange={onQuickActionsValueChange}
          value={quickActionsValue}
          type={showAmountInput ? format : 'percentage'}
          currencySymbol={currencySymbol}
          clampMax={showPercentageInput ? MAX_PERCENTAGE : undefined}
          clampMin={showPercentageInput ? 0 : undefined}
          disabled={isDisabled}
          maxDecimals={MAX_DECIMALS}
          size="medium"
          mb={0}
          label={intl.formatMessage({
            id: 'common.amount',
            defaultMessage: 'Amount'
          })}
        />
        <Button
          className={clsx(styles.updateButtonCarbon)}
          onClick={handleQuickActionsUpdate}
          disabled={isDisabled || isDisabledUpdate}
          buttonType="secondary"
          size="medium"
        >
          <FormattedMessage id="common.update" defaultMessage="Update" />
        </Button>
      </div>

      <div className={styles.container}>
        {rows.map((row, index) => (
          <FreestyleContentRow
            key={row.date}
            data={row}
            format={format}
            onSelectAll={onSelectAll}
            onSelect={onSelect}
            onValueChange={onValueChange}
            pastBlankDataValue={pastBlankDataValue}
            isDisabled={isDisabled}
            // If user is adding new driver and row before current row is current year, year after current year always needs to be opened
            isExpanded={yearsAdded.includes(row.date) || (index > 0 && isAddingNew && rows[index - 1].isCurrentYear)}
            forceExpand={lastQuickUpdatedRows[row.date]}
          />
        ))}

        {!isDisabledAddYear && (
          <div className={clsx(styles.ctaBar, styles.ctaBarNew)}>
            <Button
              onClick={e => {
                e.preventDefault();
                onAddFinancialYear?.();
              }}
              data-testid="AddFinancialYear"
              disabled={isDisabled}
              buttonType="tertiary"
              iconType="plus"
              size="medium"
            >
              <FormattedMessage id="predict.add_financial_year" defaultMessage="Add financial year" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
